import * as React from "react";
import { usePathname, useNavigate as useRouter, useSearch as useSearchParams } from "../../router";
import { ExternalLink, Box, Hash } from "../../icons";
import { WorkList, type WorkRowModel, type WorkPill } from "../work-row";
import { formatDayMonth, formatDate, agentDisplayName } from "../../format";
import { objectHref } from "../../path-label";
import { sectionColor } from "../../tokens";
import { type ScopedTask, type ViewScope } from "../../types/projects";

/**
 * The shared task slice as work rows — the ONE renderer for a list of
 * `ScopedTask` on any surface (the mode-dashboard Tasks / "Today" slices, the
 * Issues flat list). Maps each task to `WorkRowModel` (the canonical row,
 * `docs/design/WORK-ROW-SYSTEM.md`) and opens the global task peek in place
 * through its canonical `/o/<uid>` doorway when the synced uid is available.
 * Hand-built fixtures without a uid retain the legacy `?task=<id>` resolver, so
 * a row still opens on whatever page it is mounted. `scope` controls the context
 * pill — project on the workspace tier, section everywhere but a single-section
 * view. Never hand-roll a task list; render it through this.
 *
 * ── On WorkRow (2026-07-23) ──────────────────────────────────────────────────
 * This was the pivot of the app-wide WorkRow rollout. Because the component's
 * props are unchanged, every dashboard mounting `<ScopedTaskLifecycleList>`
 * (customers · operations · product · work) upgraded to the new row for free.
 * The three words WorkRow requires that `LifecycleRowModel` lacked are supplied
 * from real task data, not defaulted: the pill's `hint` is the dimension
 * ("Project"/"Section"), the owner's `name` is the server-resolved
 * `assignee_label` (id as the fallback), and the date's `hint` says whether the
 * stamp is a due date or a last-touched date.
 */

/** Two-letter avatar initials from an agent id / display name (strips `claude-`). */
export function taskInitials(who: string | null): string | undefined {
  if (!who) return undefined;
  const cleaned = who.replace(/^claude-/, "");
  const parts = cleaned.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return cleaned.slice(0, 2).toUpperCase();
}

/**
 * ScopedTask → WorkRowModel — the ONE task-row mapping, exported so the unified
 * Work list (tasks + non-task work items) reuses it instead of forking.
 * `onOpen(id)` is the fallback for rows that do not yet carry a synced uid.
 */
export function scopedTaskToRow(
  t: ScopedTask,
  { scope = { kind: "workspace" }, onOpen }: { scope?: ViewScope; onOpen: (id: number) => void },
): WorkRowModel {
  const canonicalHref = t.uid ? objectHref(t.uid) : undefined;
  // ONE tag survives the row's single tag column: the project at the workspace
  // tier, the section anywhere narrower but a single-section view (which already
  // shows the section in its own chrome). The row renders `pills[0]`; the choice
  // of WHICH tag is worth the slot at this scope is exactly the adapter's job.
  const pills: WorkPill[] = [];
  if (scope.kind === "workspace")
    pills.push({ icon: Box, label: t.project_name, hint: "Project", accent: t.project_color ?? undefined });
  else if (scope.kind !== "section")
    pills.push({ icon: Hash, label: t.section_name, hint: "Section", accent: sectionColor(t.section_slug) });

  // Due date wins the stamp when present; otherwise the last-touched date. The
  // hint says which, because "Jul 23" alone never tells you.
  const due = t.due_date ?? null;
  const stamp = due ?? t.updated_at;

  return {
    id: `task-${t.id}`,
    // Priority is the one signifier that is a real signal, not a "this is an
    // issue" label repeated 40 times — so it leads the row (WorkRow slot 1).
    signifier: { as: "priority", priority: t.priority },
    state: { as: "status", status: t.status },
    title: t.title,
    // Derived, never authored: a task in `review` cannot become done until a
    // DIFFERENT agent verifies it — the most blocked thing in this app — so it
    // carries the blocked label + edge bar wherever it is shown (#43867).
    attention: t.status === "review" ? { label: "Needs your verify", blocked: true } : undefined,
    pills: pills.length > 0 ? pills : undefined,
    // A task DOES take an assignee, so an empty one is a real gap ("unassigned"),
    // not a kind that has no owner concept.
    owner: t.assignee
      ? {
          initials: taskInitials(t.assignee)!,
          name: t.assignee_label ?? agentDisplayName(t.assignee),
          role: "Assignee",
        }
      : "unassigned",
    // Always a real date stamp, never a relative "5d ago" — one format, one
    // fixed column width down the whole list (Matt, 2026-07-20).
    date: { text: formatDayMonth(stamp), hint: `${due ? "Due" : "Updated"} · ${formatDate(stamp)}` },
    actions: [{ icon: ExternalLink, label: "Open", onClick: () => onOpen(t.id) }],
    href: canonicalHref,
    onOpen: canonicalHref ? undefined : () => onOpen(t.id),
  };
}

export function ScopedTaskLifecycleList({
  tasks,
  scope = { kind: "workspace" },
  empty = "No tasks here right now.",
  gutter = false,
}: {
  tasks: ScopedTask[];
  scope?: ViewScope;
  empty?: React.ReactNode;
  /** Inset rows to align with a card-wrapped surface's padded header (Issues list).
   *  Bare dashboard slices ("Today", section "Tasks") stay flush on the grid line. */
  gutter?: boolean;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();

  const open = React.useCallback(
    (id: number) => {
      const next = new URLSearchParams(search.toString());
      next.set("task", String(id));
      router.replace(`${pathname}?${next.toString()}`);
    },
    [router, pathname, search],
  );

  const rows = tasks.map((t) => scopedTaskToRow(t, { scope, onOpen: open }));

  return <WorkList rows={rows} empty={empty} gutter={gutter} />;
}
